import { type FormEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Crosshair, FileBarChart, Globe } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { HttpError } from '../../../shared/api/http'
import { submitContactForm } from '../api/contactClient'
import { useScrollReveal } from './useScrollReveal'

/* ── Scope items with icons ── */

const scopeItems: { key: '1' | '2' | '3'; icon: LucideIcon }[] = [
  { key: '1', icon: Crosshair },
  { key: '2', icon: Globe },
  { key: '3', icon: FileBarChart },
]

/* ── Form state ── */

interface ContactFormValues {
  name: string
  contact: string
  message: string
}

const initialFormValues: ContactFormValues = {
  name: '',
  contact: '',
  message: '',
}

/* ── Component ── */

export function PricingSection() {
  const { t } = useTranslation()
  const { ref, isVisible } = useScrollReveal({ threshold: 0.1 })

  const [form, setForm] = useState<ContactFormValues>(initialFormValues)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const canSubmit = useMemo(
    () =>
      form.name.trim() !== '' &&
      form.contact.trim() !== '' &&
      form.message.trim() !== '' &&
      !isSubmitting,
    [form.contact, form.message, form.name, isSubmitting],
  )

  const updateField = (field: keyof ContactFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (isSuccess) setIsSuccess(false)
    if (submitError) setSubmitError(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const name = form.name.trim()
    const contact = form.contact.trim()
    const message = form.message.trim()

    if (!name) {
      setSubmitError(t('landing.contact.form.errors.nameRequired'))
      return
    }
    if (!contact) {
      setSubmitError(t('landing.contact.form.errors.contactRequired'))
      return
    }
    if (!message) {
      setSubmitError(t('landing.contact.form.errors.messageRequired'))
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      await submitContactForm({
        name,
        organization: '',
        email: contact,
        message,
      })
      setIsSuccess(true)
      setForm(initialFormValues)
    } catch (error) {
      if (
        error instanceof HttpError &&
        typeof error.body === 'object' &&
        error.body !== null &&
        'error' in error.body &&
        typeof (error.body as { error?: unknown }).error === 'string'
      ) {
        setSubmitError((error.body as { error: string }).error)
      } else {
        setSubmitError(t('landing.contact.form.errors.submitFailed'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const cardClass = 'rounded-xl backdrop-blur-md bg-glass ring-1 ring-glass-ring'

  const inputClass =
    'w-full rounded-lg border border-border bg-glass-inner px-4 py-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/50 transition-all duration-150 focus:border-primary/50 focus:ring-1 focus:ring-primary/20'

  const labelClass =
    'mb-1 block text-[11px] uppercase tracking-wider text-muted-foreground'

  return (
    <section
      id="contact"
      className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
    >
      <div ref={ref} className={cn('scroll-reveal', isVisible && 'is-visible')}>
        <div className="mb-10 max-w-2xl">
          <h2 className="m-0 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            {t('landing.pricing.title')}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            {t('landing.pricing.subtitle')}
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          {/* ── Left: distilled sales info ── */}
          <article className={cn(cardClass, 'flex flex-col p-7')}>
            {/* Sub-header */}
            <h3 className="m-0 text-lg font-semibold tracking-tight text-foreground">
              {t('landing.pricing.cardTitle')}
            </h3>
            <p className="mt-2 text-sm leading-relaxed tracking-wide text-muted-foreground">
              {t('landing.pricing.cardDescription')}
            </p>

            {/* Scope checklist — icon + title + desc */}
            <div className="mt-8 grid gap-7">
              {scopeItems.map(({ key, icon: Icon }) => (
                <div key={key} className="flex items-start gap-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-icon-frame text-icon-frame-foreground ring-1 ring-icon-frame-ring">
                    <Icon size={16} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-sm font-medium leading-relaxed tracking-wide text-foreground">
                      {t(`landing.pricing.scope.${key}.title`)}
                    </div>
                    <div className="mt-0.5 text-[13px] leading-relaxed tracking-wide text-muted-foreground">
                      {t(`landing.pricing.scope.${key}.desc`)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </article>

          {/* ── Right: contact form ── */}
          <article className={cn(cardClass, 'flex flex-col p-7')}>
            <h3 className="m-0 text-lg font-semibold tracking-tight text-foreground">
              {t('landing.contact.title')}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t('landing.contact.subtitle')}
            </p>

            <form className="mt-6 grid flex-1 gap-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="contact-name" className={labelClass}>
                  {t('landing.contact.form.fields.name.label')}
                </label>
                <input
                  id="contact-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder={t('landing.contact.form.fields.name.placeholder')}
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="contact-info" className={labelClass}>
                  {t('landing.contact.form.fields.contact.label')}
                </label>
                <input
                  id="contact-info"
                  type="text"
                  value={form.contact}
                  onChange={(e) => updateField('contact', e.target.value)}
                  placeholder={t('landing.contact.form.fields.contact.placeholder')}
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="contact-message" className={labelClass}>
                  {t('landing.contact.form.fields.message.label')}
                </label>
                <textarea
                  id="contact-message"
                  value={form.message}
                  onChange={(e) => updateField('message', e.target.value)}
                  placeholder={t('landing.contact.form.fields.message.placeholder')}
                  className={cn(inputClass, 'min-h-[96px] resize-none')}
                  disabled={isSubmitting}
                />
              </div>

              {submitError && (
                <p
                  className={cn(
                    'm-0 rounded-lg px-3 py-2 text-xs',
                    'bg-sentiment-negative-surface text-sentiment-negative ring-1 ring-sentiment-negative-border',
                  )}
                >
                  {submitError}
                </p>
              )}

              {isSuccess && (
                <p
                  className={cn(
                    'm-0 rounded-lg px-3 py-2 text-xs',
                    'bg-sentiment-positive-surface text-sentiment-positive ring-1 ring-sentiment-positive-border',
                  )}
                >
                  {t('landing.contact.form.success')}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="group inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-cta text-sm font-medium text-cta-foreground shadow-[var(--cta-shadow)] transition-all duration-200 hover:bg-cta-hover hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting
                  ? t('landing.contact.form.submitting')
                  : t('landing.contact.form.submit')}
                {!isSubmitting && (
                  <ArrowRight
                    size={14}
                    className="transition-transform duration-200 group-hover:translate-x-0.5"
                  />
                )}
              </button>
            </form>
          </article>
        </div>

        {/* ── Contact footer — muted, shared below both cards ── */}
        <div className="mt-5 text-center">
          <span className="text-xs tracking-wider text-muted-foreground">
            {t('landing.pricing.contactFooter')}
          </span>
        </div>
      </div>
    </section>
  )
}
